import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../contexts/AuthContext';
import { addDoc, collection, serverTimestamp } from '@firebase/firestore';
import { db } from '../../config/firebase';
import { CAUSES } from '../layout/causes';

interface VideoMetadata {
  title: string;
  description: string;
  thumbnail: string;
  source: string;
}

interface VideoSubmitFormProps {
  onSubmitSuccess?: () => void;
}

export default function VideoSubmitForm({ onSubmitSuccess }: VideoSubmitFormProps) {
  const { currentUser } = useAuth();
  const [videoUrl, setVideoUrl] = useState('');
  const [selectedCauses, setSelectedCauses] = useState<string[]>([]);
  const [primaryCause, setPrimaryCause] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
  const [validating, setValidating] = useState(false);

  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const validateVideoUrl = (url: string): { isValid: boolean; error?: string } => {
    if (!url.trim()) {
      return { isValid: false, error: 'Please enter a video URL.' };
    }

    const videoId = extractVideoId(url);
    if (!videoId) {
      return { isValid: false, error: 'Please provide a valid YouTube URL.' };
    }

    return { isValid: true };
  };

  const extractVideoMetadata = async (url: string): Promise<VideoMetadata> => {
    const videoId = extractVideoId(url);
    if (!videoId) {
      throw new Error('Invalid YouTube URL');
    }

    try {
      const response = await fetch(`https://www.youtube.com/oembed?url=http://www.youtube.com/watch?v=${videoId}&format=json`);
      if (!response.ok) {
        throw new Error('Failed to fetch video information');
      }
      
      const data = await response.json();
      return {
        title: data.title || 'Untitled Video',
        description: data.description || 'No description available',
        thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        source: 'youtube.com'
      };
    } catch (error) {
      console.error('Error extracting video metadata:', error);
      return {
        title: 'Video from YouTube',
        description: 'No description available - metadata extraction failed',
        thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        source: 'youtube.com'
      };
    }
  };

  const handleUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setVideoUrl(url);
    setError(null);
    setMetadata(null);

    if (url.trim()) {
      setValidating(true);
      try {
        const validation = validateVideoUrl(url);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid video URL');
          return;
        }

        // Extract metadata
        const extractedMetadata = await extractVideoMetadata(url);
        setMetadata(extractedMetadata);
        
        // Show a warning if metadata extraction failed (fallback was used)
        if (extractedMetadata.title === 'Video from YouTube') {
          setError('Warning: Could not extract video details automatically. You can still share the video, but some information may be missing.');
        }
      } catch (err) {
        setError('Failed to validate video URL. Please try again.');
      } finally {
        setValidating(false);
      }
    }
  };

  const handleCauseChange = (e: SelectChangeEvent<string>) => {
    const cause = e.target.value;
    if (cause && !selectedCauses.includes(cause)) {
      if (selectedCauses.length >= 3) {
        setError('You can only select a maximum of 3 causes.');
        return;
      }
      setSelectedCauses(prev => [...prev, cause]);
      if (!primaryCause) {
        setPrimaryCause(cause);
      }
    }
  };

  const handlePrimaryCauseChange = (e: SelectChangeEvent<string>) => {
    setPrimaryCause(e.target.value);
  };

  const handleRemoveCause = (causeToRemove: string) => {
    setSelectedCauses(prev => prev.filter(cause => cause !== causeToRemove));
    if (primaryCause === causeToRemove) {
      setPrimaryCause(selectedCauses.find(cause => cause !== causeToRemove) || '');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be signed in to share videos.');
      return;
    }

    if (!videoUrl.trim()) {
      setError('Please enter a video URL.');
      return;
    }

    if (!primaryCause) {
      setError('Please select a primary cause.');
      return;
    }

    if (selectedCauses.length === 0) {
      setError('Please select at least one cause.');
      return;
    }

    if (selectedCauses.length > 3) {
      setError('You can only select a maximum of 3 causes.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const videoId = extractVideoId(videoUrl);
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }

      const videoPost = {
        videoId,
        title: metadata?.title || 'Untitled Video',
        description: metadata?.description || 'No description available',
        thumbnail: metadata?.thumbnail || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        date: serverTimestamp(),
        causes: selectedCauses,
        primaryCause,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userAvatar: currentUser.photoURL || '',
        reactions: {},
        commentCount: 0,
        lastUpdated: serverTimestamp()
      };

      await addDoc(collection(db, 'videos'), videoPost);
      setSuccess(true);
      setVideoUrl('');
      setSelectedCauses([]);
      setPrimaryCause('');
      setMetadata(null);
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to share video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Video shared successfully!
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => setSuccess(false)}
        >
          Share Another Video
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share videos from YouTube. We accept videos from any YouTube channel that discusses relevant topics.
      </Typography>

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Video URL"
          placeholder="https://www.youtube.com/watch?v=..."
          value={videoUrl}
          onChange={handleUrlChange}
          disabled={loading}
          error={!!error && !validating}
          helperText={error && !validating ? error : ''}
        />

        {validating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Validating video...
            </Typography>
          </Box>
        )}

        {metadata && (
          <Card sx={{ mb: 2 }}>
            <CardMedia
              component="img"
              height="140"
              image={metadata.thumbnail}
              alt={metadata.title}
              sx={{ objectFit: 'cover' }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {metadata.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Source: {metadata.source}
              </Typography>
            </CardContent>
          </Card>
        )}

        <FormControl fullWidth>
          <InputLabel>Add Causes</InputLabel>
          <Select
            value=""
            label="Add Causes"
            onChange={handleCauseChange}
            disabled={loading || selectedCauses.length >= 3}
          >
            {CAUSES.map((cause) => (
              <MenuItem key={cause.id} value={cause.id}>
                {cause.name}
              </MenuItem>
            ))}
          </Select>
          {selectedCauses.length >= 3 && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              Maximum of 3 causes reached
            </Typography>
          )}
        </FormControl>

        {selectedCauses.length > 0 && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Selected Causes:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedCauses.map((cause) => (
                <Chip
                  key={cause}
                  label={CAUSES.find(c => c.id === cause)?.name || cause}
                  onDelete={() => handleRemoveCause(cause)}
                  color={primaryCause === cause ? 'primary' : 'default'}
                  variant={primaryCause === cause ? 'filled' : 'outlined'}
                  onClick={() => setPrimaryCause(cause)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {selectedCauses.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Primary Cause</InputLabel>
            <Select
              value={primaryCause}
              label="Primary Cause"
              onChange={handlePrimaryCauseChange}
              disabled={loading}
            >
              {selectedCauses.map((cause) => (
                <MenuItem key={cause} value={cause}>
                  {CAUSES.find(c => c.id === cause)?.name || cause}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading || !videoUrl.trim() || !primaryCause || selectedCauses.length === 0}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Share Video'}
        </Button>
      </Stack>
    </Box>
  );
} 